import { type Ref, ref, computed } from "vue";

import { graphGet } from "@/helpers/api/apiGraph";
import DriveItem from "@/models/Documents/DriveItem";
import DriveItemHelper from "@/models/helpers/DriveItemHelper";

import { useFeaturesStore } from "@/stores/features";

interface DriveItemRoot {
  nctSystem?: unknown;
  nctGroup?: unknown;
  nctType?: unknown;
  nctIdentifier?: unknown;
  nctBehavior?: unknown;
  nctBehaviour?: unknown;
}

interface DriveItemsComposableDependencies {
  // Lazy lookup so the composable can reference functions defined later in
  // the store setup (e.g. calculateDriveItemsRoot from drive-items-hierarchy).
  getCalculateDriveItemsRoot: () => (driveId: string, itemId: string) => Promise<DriveItemRoot | null>;
}

export const useDriveItemsComposable = (dependencies: DriveItemsComposableDependencies) => {
  const driveMap: Ref<Record<string, Record<string, DriveItem>>> = ref({});
  const pathToDriveItemMap: Ref<Record<string, Record<string, string>>> = ref({});

  const driveItem = computed(() => (driveId: string, itemId: string) => {
    if (driveMap.value[driveId] && driveMap.value[driveId][itemId]) {
      return driveMap.value[driveId][itemId];
    }
    return null;
  });

  const driveItemChildren = computed(() => (driveId: string, itemId: string) => {
    if (driveMap.value[driveId] && driveMap.value[driveId][itemId]) {
      return driveMap.value[driveId][itemId].childrenList;
    }
    return [];
  });

  const parentDriveItem = computed(() => (driveId: string, itemId: string) => {
    for (const candidateItemId in driveMap.value[driveId]) {
      const item = driveMap.value[driveId][candidateItemId] as DriveItem & { error?: unknown };
      if (!item.error && item.childrenList?.some((child: DriveItem) => child.id === itemId)) {
        return driveMap.value[driveId][candidateItemId];
      }
    }
    return null;
  });

  const searchStateForDriveItem = computed(() => (driveId: string, itemId: string) => {
    if (driveMap.value[driveId]) {
      if (driveMap.value[driveId][itemId]) {
        return driveMap.value[driveId][itemId];
      }
      Object.keys(driveMap.value[driveId]).forEach(key => {
        const foundItem = driveMap.value[driveId][key].childrenList?.find((child: DriveItem) => child.id === itemId);
        if (foundItem) {
          return foundItem;
        }
      });
    }
    return null;
  });

  const fetchDriveItem = async (
    driveId: string,
    itemId: string
  ): Promise<DriveItem & { error?: { code: string }; children?: DriveItem[] }> => {
    if (!driveId) {
      return {
        error: { code: "DriveItem.Http404ItemNotFound" }
      } as DriveItem & { error?: { code: string }; children?: DriveItem[] };
    }
    const featureStore = useFeaturesStore();
    let driveItemFetched: DriveItem & { error?: { code: string }; children?: DriveItem[] };
    try {
      if (featureStore.isFeatureEnabled("SensitivityLabel") && import.meta.env.VITE_BUILD_TARGET !== "kkfiler") {
        const driveItemLoaded = await graphGet(`/drives/${driveId}/items/${DriveItemHelper.getItemId(itemId)}`);
        driveItemFetched = new DriveItem(driveItemLoaded);
        if (driveItemFetched.folder) {
          const driveItemChildrenResp = await graphGet(
            `/drives/${driveId}/items/${DriveItemHelper.getItemId(
              itemId
            )}/children?$expand=listItem($expand=fields($select=_DisplayName))`
          );
          driveItemFetched.children = driveItemChildrenResp.value;
        }
      } else {
        const driveItemLoaded = await graphGet(
          `/drives/${driveId}/items/${DriveItemHelper.getItemId(itemId)}/?$expand=children`
        );
        driveItemFetched = new DriveItem(driveItemLoaded);
      }

      if (
        driveMap.value[driveId] &&
        driveMap.value[driveId][itemId] &&
        !(driveMap.value[driveId][itemId] as DriveItem & { error?: unknown }).error
      ) {
        const replace = driveMap.value[driveId][itemId];
        driveItemFetched.nctSystem = replace.nctSystem;
        driveItemFetched.nctType = replace.nctType;
        driveItemFetched.nctGroup = replace.nctGroup;
        driveItemFetched.nctIdentifier = replace.nctIdentifier;
        driveItemFetched.nctBehaviour = replace.nctBehaviour;

        driveItemFetched.childrenList?.forEach((child: DriveItem) => {
          child.nctSystem = replace.nctSystem;
          child.nctType = replace.nctType;
          child.nctGroup = replace.nctGroup;
          child.nctIdentifier = replace.nctIdentifier;
          child.nctBehaviour = replace.nctBehaviour;
        });
      }
      return driveItemFetched;
    } catch (caughtError: unknown) {
      const errorWithResponse = caughtError as { response?: { status?: number } };
      if (
        errorWithResponse.response &&
        (errorWithResponse.response.status === 401 ||
          errorWithResponse.response.status === 404 ||
          errorWithResponse.response.status === 403)
      ) {
        return {
          id: itemId,
          parentReference: { driveId: driveId },
          error: { code: "DriveItem.Http404ItemNotFound" }
        } as DriveItem & { error?: { code: string }; children?: DriveItem[] };
      }
      return null as unknown as DriveItem & { error?: { code: string }; children?: DriveItem[] };
    }
  };

  const reloadDriveItem = async (driveId: string, itemId: string, additionalProperties?: Record<string, unknown>) => {
    const driveItemFetched = await fetchDriveItem(driveId, DriveItemHelper.getItemId(itemId));

    if (driveItemFetched && !driveItemFetched.error) {
      if (additionalProperties) {
        Object.assign(driveItemFetched, additionalProperties);
        driveItemFetched.children?.forEach((child: DriveItem) => {
          Object.assign(child, additionalProperties);
        });
      }

      if (!driveItemFetched.nctType) {
        const calculateDriveItemsRoot = dependencies.getCalculateDriveItemsRoot();
        const rootOf = await calculateDriveItemsRoot(driveItemFetched.parentReference.driveId, driveItemFetched.id);
        if (rootOf) {
          driveItemFetched.nctSystem = rootOf.nctSystem;
          driveItemFetched.nctGroup = rootOf.nctGroup;
          driveItemFetched.nctType = rootOf.nctType;
          driveItemFetched.nctIdentifier = rootOf.nctIdentifier;
          driveItemFetched.nctBehavior = rootOf.nctBehavior;

          driveItemFetched.childrenList?.forEach((child: DriveItem) => {
            child.nctSystem = rootOf.nctSystem;
            child.nctType = rootOf.nctType;
            child.nctGroup = rootOf.nctGroup;
            child.nctIdentifier = rootOf.nctIdentifier;
            child.nctBehaviour = rootOf.nctBehaviour;
          });
        }
      }
    }

    if (!driveMap.value[driveId]) {
      driveMap.value[driveId] = {};
    }
    driveMap.value[driveId][itemId] = driveItemFetched;

    return driveMap.value[driveItemFetched?.parentReference?.driveId][driveItemFetched?.id];
  };

  const loadDriveItem = async (driveId: string, itemId: string, additionalProperties?: Record<string, unknown>) => {
    if (!driveMap.value[driveId] || !driveMap.value[driveId][DriveItemHelper.getItemId(itemId)]) {
      await reloadDriveItem(driveId, itemId, additionalProperties);
    }
    return driveMap.value[driveId][DriveItemHelper.getItemId(itemId)];
  };

  const reloadDriveItemWithoutDecorationgWithNCTProps = async (driveId: string, itemId: string) => {
    const driveItemFetched = await fetchDriveItem(driveId, itemId);
    if (!driveMap.value[driveId]) {
      driveMap.value[driveId] = {};
    }
    driveMap.value[driveId][itemId] = driveItemFetched;
    return driveItemFetched;
  };

  const loadDriveItemWithoutDecorationgWithNCTProps = async (driveId: string, itemId: string) => {
    if (!driveMap.value[driveId] || !driveMap.value[driveId][DriveItemHelper.getItemId(itemId)]) {
      await reloadDriveItemWithoutDecorationgWithNCTProps(driveId, itemId);
    }
    return driveMap.value[driveId][DriveItemHelper.getItemId(itemId)];
  };

  const setNCTMetaOnStoreDriveItems = (driveId: string, itemId: string, metaproperties: Record<string, unknown>) => {
    let item = driveItem.value(driveId, itemId);
    if (item) {
      item = Object.assign(item, metaproperties);
      item.childrenList?.forEach((child: DriveItem) => (child = Object.assign(child, metaproperties)));
      driveMap.value[driveId][itemId] = item;
    }
  };

  const reloadDriveItemFromPath = async (
    driveId: string,
    path: string,
    additionalProperties: Record<string, unknown> = {}
  ) => {
    try {
      const featureStore = useFeaturesStore();
      let driveItemFetched: DriveItem & { children?: DriveItem[] };

      if (featureStore.isFeatureEnabled("SensitivityLabel") && import.meta.env.VITE_BUILD_TARGET !== "kkfiler") {
        const driveItemLoaded = await graphGet(`/drives/${driveId}/root:${path}`);
        driveItemFetched = new DriveItem(driveItemLoaded);
        const driveItemChildrenResp = await graphGet(
          `/drives/${driveId}/items/${DriveItemHelper.getItemId(
            driveItemFetched.id
          )}/children?$expand=listItem($expand=fields($select=_DisplayName))`
        );
        driveItemFetched.children = driveItemChildrenResp.value;
      } else {
        const driveItemLoaded = await graphGet(`/drives/${driveId}/root:/${path}/?$expand=children`);
        driveItemFetched = new DriveItem(driveItemLoaded);
      }

      if (additionalProperties) {
        Object.assign(driveItemFetched, additionalProperties);
        driveItemFetched.children?.forEach((child: DriveItem) => Object.assign(child, additionalProperties));
      }

      if (!driveItemFetched.nctType) {
        const calculateDriveItemsRoot = dependencies.getCalculateDriveItemsRoot();
        const rootOf = await calculateDriveItemsRoot(driveId, driveItemFetched.id);
        if (rootOf) {
          driveItemFetched.nctSystem = rootOf.nctSystem;
          driveItemFetched.nctGroup = rootOf.nctGroup;
          driveItemFetched.nctType = rootOf.nctType;
          driveItemFetched.nctIdentifier = rootOf.nctIdentifier;
          driveItemFetched.nctBehavior = rootOf.nctBehavior;

          driveItemFetched.childrenList?.forEach((child: DriveItem) => {
            child.nctSystem = rootOf.nctSystem;
            child.nctType = rootOf.nctType;
            child.nctGroup = rootOf.nctGroup;
            child.nctIdentifier = rootOf.nctIdentifier;
            child.nctBehaviour = rootOf.nctBehaviour;
          });
        }
      }

      if (!driveMap.value[driveId]) {
        driveMap.value[driveId] = {};
      }
      if (!pathToDriveItemMap.value[driveId]) {
        pathToDriveItemMap.value[driveId] = {};
      }

      driveMap.value[driveId][driveItemFetched.id] = driveItemFetched;
      pathToDriveItemMap.value[driveId][path] = driveItemFetched.id;
      return driveItemFetched;
    } catch {
      if (!pathToDriveItemMap.value[driveId]) {
        pathToDriveItemMap.value[driveId] = {};
      }
      pathToDriveItemMap.value[driveId][path] = "";
      return null;
    }
  };

  const loadDriveItemFromPath = async (
    driveId: string,
    path: string,
    additionalProperties?: Record<string, unknown>
  ) => {
    if (!pathToDriveItemMap.value[driveId] || !pathToDriveItemMap.value[driveId][path]) {
      await reloadDriveItemFromPath(driveId, path, additionalProperties);
    }
    const itemId = pathToDriveItemMap.value[driveId][path];
    if (itemId) {
      return loadDriveItem(driveId, itemId, additionalProperties);
    }
    return null;
  };

  const openDriveItem = async (driveId: string, itemId: string) => {
    const item = await loadDriveItem(driveId, itemId);
    if (item && item.webUrl) {
      window.open(item.webUrl, "_blank");
    }
  };

  return {
    driveMap,
    pathToDriveItemMap,
    driveItem,
    driveItemChildren,
    parentDriveItem,
    searchStateForDriveItem,
    fetchDriveItem,
    loadDriveItem,
    reloadDriveItem,
    loadDriveItemWithoutDecorationgWithNCTProps,
    reloadDriveItemWithoutDecorationgWithNCTProps,
    setNCTMetaOnStoreDriveItems,
    loadDriveItemFromPath,
    reloadDriveItemFromPath,
    openDriveItem
  };
};
